/**
 * Extract pipe-size / station / direction tokens out of a TXT filename.
 *
 * Source-of-truth contract from the PRD:
 *   - The TXT body's `Stn #` and `Bug` columns are too messy to rely on
 *     (single-digit machine ID vs human label `ST1`; `CCW / Ditch Sid`
 *     style strings); the filename is the authoritative source for station
 *     and direction.
 *   - Pipe size in the filename is only a SANITY CHECK against the pipe
 *     set's `nominalOd`. Pipe-set size is never overwritten from the
 *     filename.
 *
 * Parsing is forgiving: tokens may appear in any order, any case, and may
 * be separated by spaces, underscores, hyphens, or dots. Missing tokens
 * return `null` (the dialog flags such files as "unverified" but allows
 * upload).
 */
export type WeldDirection = 'CCW' | 'CW'

export interface WeldTxtFilenameMeta {
  /** Numeric pipe size in inches, or `null` when the filename has no `<N>in/inch/"` token. */
  pipeSize: number | null
  /** Numeric station, or `null` when the filename has no `ST<N>` token. */
  station: number | null
  /** Weld direction, or `null` when the filename has no `CCW`/`CW` token. */
  direction: WeldDirection | null
}

// Use explicit non-letter separators rather than `\b`, because `_` counts as
// a word character in regex \b semantics — that would miss tokens in
// `weld_log_ST_3_14IN_CW.txt` style names.
const SEP_BEFORE = '(?:^|[^a-zA-Z])'
const SEP_AFTER = '(?:[^a-zA-Z]|$)'

const PIPE_SIZE_RE = new RegExp(
  `${SEP_BEFORE}(\\d+(?:\\.\\d+)?)\\s*(?:inch(?:es)?|in|")${SEP_AFTER}`,
  'i'
)
const STATION_RE = new RegExp(`${SEP_BEFORE}st[\\s_-]*?(\\d+)${SEP_AFTER}`, 'i')
const DIRECTION_RE = new RegExp(`${SEP_BEFORE}(ccw|cw)${SEP_AFTER}`, 'i')

export function parseWeldTxtFilename(filename: string | null | undefined): WeldTxtFilenameMeta {
  const stem = stripExtension(String(filename ?? ''))
  return {
    pipeSize: matchPipeSize(stem),
    station: matchStation(stem),
    direction: matchDirection(stem)
  }
}

function stripExtension(filename: string): string {
  return filename.replace(/\.[^./\\]+$/, '')
}

function matchPipeSize(stem: string): number | null {
  const m = PIPE_SIZE_RE.exec(stem)
  if (!m) return null
  const n = Number(m[1])
  return Number.isFinite(n) && n > 0 ? n : null
}

function matchStation(stem: string): number | null {
  const m = STATION_RE.exec(stem)
  if (!m) return null
  const n = Number(m[1])
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null
}

function matchDirection(stem: string): WeldDirection | null {
  const m = DIRECTION_RE.exec(stem)
  if (!m) return null
  return m[1].toUpperCase() as WeldDirection
}

/**
 * Format a station number + direction for display in the Weld Parameters
 * page-header subtitle. Returns `null` when either is missing so the
 * caller can omit the segment entirely.
 */
export function formatStationDirectionLabel(
  station: number | null | undefined,
  direction: WeldDirection | null | undefined
): string | null {
  if (station === null || station === undefined) return null
  if (!direction) return null
  return `Station ${station} ${direction}`
}
